import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Space,
  Button,
  Radio,
  Typography,
  message,
  Alert,
} from "@mimber/ui-web"
import { StopOutlined } from "@mimber/ui-web"
import { Form, RadioGroupField } from "@mimber/forms"
import { useCancelEvent } from "@mimber/client/mimber-organization"
import { EventEditScope } from "@mimber/schema"
import { CancelEventFormSchema } from "./CancelEventForm.schema"
import type { CancelEventFormValues } from "./CancelEventForm.schema"
import { SCOPE_OPTIONS } from "./CancelEventForm.utils"
import type { CancelEventFormProps } from "./CancelEventForm.types"

const { Text, Paragraph } = Typography

export const CancelEventForm: React.FunctionComponent<CancelEventFormProps> = ({
  event,
  onSuccess,
  onCancel,
}) => {
  const isInSeries = !!event.eventSeriesId

  const form = useForm<CancelEventFormValues>({
    resolver: zodResolver(CancelEventFormSchema),
    mode: "onBlur",
    defaultValues: {
      scope: EventEditScope.THIS_EVENT,
    },
  })

  const mutation = useCancelEvent(event.uuid, {
    onSuccess: () => {
      message.success("Event cancelled successfully")
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to cancel event: ${error.message}`)
    },
  })

  const onSubmit = (data: CancelEventFormValues) => {
    mutation.mutate(isInSeries ? { scope: data.scope } : {})
  }

  return (
    <Form form={form} onSubmit={onSubmit} layout="vertical">
      <Alert
        type="warning"
        title="Event will be cancelled"
        description="Cancelling this event will prevent new registrations and notify registered members."
        showIcon
      />
      <Paragraph>
        Are you sure you want to cancel <Text strong>{event.title}</Text>?
      </Paragraph>

      {isInSeries && (
        <>
          <Paragraph type="secondary">
            This event is part of a series. Which events do you want to cancel?
          </Paragraph>
          <RadioGroupField name="scope" disabled={mutation.isPending}>
            <Space direction="vertical">
              {SCOPE_OPTIONS.map((opt) => (
                <Radio key={opt.value} value={opt.value}>
                  <Space direction="vertical" size={0}>
                    <span>{opt.label}</span>
                    <Paragraph
                      type="secondary"
                      style={{ marginBottom: 0, fontSize: 12 }}
                    >
                      {opt.description}
                    </Paragraph>
                  </Space>
                </Radio>
              ))}
            </Space>
          </RadioGroupField>
        </>
      )}

      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button onClick={onCancel} disabled={mutation.isPending}>
          Back
        </Button>
        <Button
          type="primary"
          danger
          icon={<StopOutlined />}
          loading={mutation.isPending}
          disabled={mutation.isPending}
          htmlType="submit"
        >
          Cancel Event
        </Button>
      </Space>
    </Form>
  )
}
